/**
 * The date-and-hour a phase gives back to the procedure step that wrote it.
 *
 * Extracted from `usePhases` so the rule is testable: that module reaches the
 * API client and cannot be imported by a test, which is why nothing caught the
 * bug this file exists to prevent.
 *
 * A scheduled procedure persists the hour she chose as the phase's `startTime`
 * (see the `status === "scheduled"` write in `CycleDetailScreen`). Reading
 * `startDate` without it silently rounds her appointment to a day — which is
 * how the egg-retrieval outcome form came back with an empty time field over an
 * appointment she had already dated to the minute.
 */

/**
 * `HH:mm`, or null for anything this cannot read as a clock time.
 *
 * The phase contract types `startTime` as a bare string with no format, and
 * nothing in this codebase trusts it: `postProcedureTransition` slices it to
 * five characters before showing it, and the write path slices it again. A
 * Postgres `time` column's natural JSON form is `"09:00:00"`, which appended
 * raw would build `...T09:00:00:00` — a value the picker cannot parse and
 * `new Date()` reads as `NaN`, so saving would drop the date and the hour
 * together.
 *
 * Returning null on anything unrecognized is the safe failure: the caller then
 * reports the day alone, which is honest, rather than a corrupt timestamp.
 */
function clockTime(value: string | null | undefined): string | null {
  if (!value) return null;
  const match = /^(\d{1,2}):(\d{2})/.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return `${hours.toString().padStart(2, "0")}:${match[2]}`;
}

/**
 * The value a step's date field (`procedureDate` / `transferDate` /
 * `collectionDate`) takes from its phase, or null when the phase carries no
 * date at all.
 *
 * Returns a bare `YYYY-MM-DD` when only the day is known. A caller must not
 * invent an hour for it: a fabricated `T00:00` reads as "booked for midnight",
 * a time no clinic gave her.
 */
export function resolvePhaseProcedureDateValue({
  startDate,
  startTime,
}: {
  startDate: string | null | undefined;
  startTime?: string | null;
}): string | null {
  if (!startDate) return null;
  const day = startDate.slice(0, 10);
  const time = clockTime(startTime);
  return time ? `${day}T${time}:00` : day;
}
